import CaseStudyCard from '../components/CaseStudyCard';
import CertificationsGrid from '../components/CertificationsGrid';
import IndustryCard from '../components/IndustryCard';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import { Icon, IconId } from '../components/icons';
import { CASE_STUDIES } from '../data/caseStudies';
import { INDUSTRIES } from '../data/industries';
import { SITE } from '../data/site';
import { TEAM } from '../data/team';
import { usePageMeta } from '../hooks/usePageMeta';
import './About.css';

const TIMELINE: { year: string; title: string; desc: string }[] = [
  {
    year: '2024',
    title: 'Founded in Toronto',
    desc: 'Started as a small studio doing web development and a mix of Microsoft services for local businesses and nonprofits.',
  },
  {
    year: 'Early 2025',
    title: 'Web development',
    desc: 'Full-stack websites and apps for startups — designed, built, and kept running.',
  },
  {
    year: 'Late 2025',
    title: 'Power Platform',
    desc: 'Onboarding clients onto Microsoft 365 and building our first Power Platform automations for businesses struggling with manual work.',
  },
  {
    year: '2026',
    title: 'Copilot & Power Platform engagements',
    desc: 'Copilot Studio agents, AI document intake, and workflow automation on the Microsoft tools clients already use.',
  },
  {
    year: 'Today',
    title: 'Fully Microsoft + AI automation',
    desc: 'Every engagement is now Microsoft Copilot, Power Platform, Azure AI, or workflow automation — built to run in production, not to demo.',
  },
];

const VALUES = [
  'Ship things that run in production, not slideware',
  'Security and governance designed in, never bolted on',
  'Choose the right tool, even when it isn’t Microsoft',
  'Straight answers on scope, timeline, and price',
];

const PRINCIPLES: { title: string; desc: string; icon: IconId }[] = [
  {
    title: 'Security-First Architecture',
    desc: 'AI built with enterprise security, compliance, and data boundaries from day one.',
    icon: 'security',
  },
  {
    title: 'Governance Embedded',
    desc: "Policies, monitoring, and compliance aren't afterthoughts — they're foundational.",
    icon: 'governance',
  },
  {
    title: 'Microsoft-Deep, Tool-Agnostic',
    desc: 'Specialists in Copilot Studio, Power Platform, Azure AI, and M365 — and fluent in n8n and open automation stacks when they’re the better fit.',
    icon: 'microsoft',
  },
  {
    title: 'Small, Responsive Team',
    desc: 'No handoffs. No offshore teams. Senior consultants working directly with your team.',
    icon: 'advisory',
  },
  {
    title: 'Fast Delivery',
    desc: 'Agile delivery with clear milestones. See value in weeks, not quarters.',
    icon: 'speed',
  },
  {
    title: `Based in ${SITE.location.city}`,
    desc: 'Local expertise. Canadian data residency knowledge. On-time in your timezone.',
    icon: 'location',
  },
];

function About() {
  usePageMeta({
    title: 'About | EightyThreeLabs',
    description: `${SITE.name} is a Microsoft-certified AI automation consultancy founded in ${SITE.founded} in ${SITE.location.city}, serving professional services, nonprofits, manufacturing, and healthcare.`,
  });

  const lead = TEAM[0];

  return (
    <Layout>
      <PageHeader
        eyebrow="ABOUT US"
        title="Built in Toronto. Focused on automation that runs."
        description={`Founded in ${SITE.founded}, Microsoft-certified, and working with professional services firms, nonprofits, manufacturers, and clinics across North America.`}
      />

      {/* 01 STORY + TIMELINE */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="01"
            eyebrow="OUR STORY"
            title="From building websites to building on Microsoft."
            align="left"
          />
          <div className="about-story-grid reveal">
            <div className="about-story">
              <p className="about-paragraph">
                EightyThreeLabs started in {SITE.founded} in {SITE.location.city} as a web
                development studio — full-stack websites and apps for startups, designed, built, and
                kept running.
              </p>
              <p className="about-paragraph">
                Along the way we started onboarding clients onto Microsoft 365 — setting up tenants,
                email, Teams, and SharePoint — and helping companies that were struggling to adopt
                AI and automation. That work kept showing us the same thing: most businesses already
                had their data in Microsoft — SharePoint, Dataverse, Excel, a user base in Entra —
                and were still moving it around by hand.
              </p>
              <p className="about-paragraph">
                So we made the switch. We focus on Microsoft because we genuinely love the platform,
                and because we love solving problems and helping businesses get real work off their
                plate. The best projects start the same way: you come to us with something specific
                you want to automate — a spreadsheet process, an approval chain, a Monday report —
                and we build it on the tools you already pay for. If your data isn&rsquo;t in
                Microsoft yet, we handle the migration too.
              </p>
              <p className="about-paragraph">
                Today, every engagement is Copilot, Power Platform, Azure AI, or workflow automation
                — designed to run in production from day one. No shortcuts. No experiments. Just
                results.
              </p>
            </div>

            <ol className="about-timeline">
              {TIMELINE.map(item => (
                <li className="about-timeline-item" key={item.title}>
                  <span className="about-timeline-year">{item.year}</span>
                  <h4 className="about-timeline-title">{item.title}</h4>
                  <p className="about-timeline-desc">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 02 MISSION / VISION / VALUES */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="02"
            eyebrow="WHY WE EXIST"
            title={'Mission, vision, and what we won’t compromise on'}
            align="left"
          />
          <div className="about-mvv-grid reveal">
            <div className="service-card">
              <span className="about-mvv-label">Mission</span>
              <h3 className="service-title">Make AI automation actually run</h3>
              <p className="service-description">
                Give small and mid-sized organizations the same governed, production-grade
                automation that enterprises get — without the enterprise price tag or the six-month
                pilot.
              </p>
            </div>
            <div className="service-card">
              <span className="about-mvv-label">Vision</span>
              <h3 className="service-title">Businesses where nobody re-keys anything</h3>
              <p className="service-description">
                A world where the tools you already pay for talk to each other, the repetitive work
                runs itself, and your people spend their time on judgment calls, not data entry.
              </p>
            </div>
            <div className="service-card">
              <span className="about-mvv-label">Values</span>
              <h3 className="service-title">How we work</h3>
              <ul className="segment-list">
                {VALUES.map(v => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 03 CREDENTIALS */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="03"
            eyebrow="CREDENTIALS"
            title="Microsoft-certified across AI, data, and the Power Platform"
            subtitle="Five current Microsoft certifications spanning Azure AI engineering, Power Platform, Azure data, Dynamics 365, and Azure fundamentals."
            align="left"
          />
          <div className="reveal">
            <CertificationsGrid />
          </div>
          <div className="about-facts-row reveal reveal-delay-1">
            <div className="stat-card">
              <div className="stat-number">{SITE.founded}</div>
              <div className="stat-label">Founded in {SITE.location.city}</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{SITE.certifications.length}</div>
              <div className="stat-label">Microsoft Certifications</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">2 – 6 Weeks</div>
              <div className="stat-label">Time to First Automation</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">25% – 40%</div>
              <div className="stat-label">Efficiency Gains</div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 INDUSTRIES */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="04"
            eyebrow="INDUSTRIES WE SERVE"
            title="Where the manual work piles up"
            subtitle="Four verticals where document-heavy, approval-heavy processes are the norm — and where automation pays back fastest."
            align="left"
          />
          <div className="industry-grid reveal">
            {INDUSTRIES.map(industry => (
              <IndustryCard key={industry.id} industry={industry} />
            ))}
          </div>
        </div>
      </section>

      {/* 05 SELECTED RESULTS */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="05"
            eyebrow="SELECTED RESULTS"
            title="What it looks like when it runs"
            subtitle="Anonymized engagements — the shape of the problem, what we built, and what changed."
            align="left"
          />
          <div className="case-grid reveal">
            {CASE_STUDIES.map(study => (
              <CaseStudyCard key={study.id} study={study} />
            ))}
          </div>
        </div>
      </section>

      {/* 06 PRINCIPLES */}
      <section className="section section-gray">
        <div className="container">
          <SectionHeader
            number="06"
            eyebrow="WHAT WE STAND FOR"
            title="Built for Outcomes, Not Slideware"
            align="left"
          />
          <div className="features-grid reveal">
            {PRINCIPLES.map(p => (
              <div className="feature-item" key={p.title}>
                <div
                  className={`feature-icon${p.icon === 'microsoft' ? ' feature-icon-product' : ''}`}
                >
                  <Icon id={p.icon} size={p.icon === 'microsoft' ? 28 : 24} />
                </div>
                <div className="feature-content">
                  <h4 className="feature-title">{p.title}</h4>
                  <p className="feature-desc">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 07 LEADERSHIP */}
      <section className="section section-white">
        <div className="container">
          <SectionHeader
            number="07"
            eyebrow={'WHO YOU’LL WORK WITH'}
            title="A senior consultant, not a rotating cast"
            align="left"
          />
          <div className="about-bio-card reveal">
            <div className="about-bio-avatar" aria-hidden={!lead.photo}>
              {lead.photo ? <img src={lead.photo} alt={lead.name} /> : lead.name.charAt(0)}
            </div>
            <div>
              <h3 className="about-bio-name">{lead.name}</h3>
              <p className="about-bio-role">{lead.role}</p>
              <p className="about-bio-background">{lead.summary}</p>
              <div className="about-bio-links">
                <a href={`mailto:${lead.email}`} className="contact-link">
                  {lead.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default About;
